import {
    HeadContent,
    Outlet,
    Scripts,
    createRootRoute
} from '@tanstack/solid-router';
import { createServerFn } from '@tanstack/solid-start';
import { Show, createEffect, onMount } from 'solid-js';
import { HydrationScript } from 'solid-js/web';
import { z } from 'zod';

import { initPostHog } from '~/lib/helpers/posthog';
import { locale } from '~/lib/i18n';
import { captureException, initSentry } from '~/lib/sentry/client';
import { CalculatorProvider, useCalculator } from '~/lib/store';
import appCss from '~/styles/app.css?url';

// ---------------------------------------------------------------------------
// Search params (attribution + referral)
// ---------------------------------------------------------------------------

const searchSchema = z.object({
    utm_source: z.string().optional(),
    utm_medium: z.string().optional(),
    utm_campaign: z.string().optional(),
    utm_term: z.string().optional(),
    utm_content: z.string().optional(),
    referralCode: z.string().optional()
});

export type CalculatorSearch = z.infer<typeof searchSchema>;

export interface AppConfig {
    googleApiKey: string;
    googleTag: string;
    whatsappLink: string;
    posthogKey: string;
    posthogHost: string;
    sentryDsn: string;
    sentryEnvironment: string;
}

/** Read public config from server env vars at runtime. */
const getPublicConfig = createServerFn({ method: 'GET' }).handler(
    async (): Promise<AppConfig> => ({
        googleApiKey: process.env.GOOGLE_API_KEY ?? '',
        googleTag: process.env.GOOGLE_TAG ?? '',
        whatsappLink: process.env.WHATSAPP_LINK ?? '',
        posthogKey: process.env.POSTHOG_KEY ?? '',
        posthogHost: process.env.POSTHOG_HOST ?? '',
        sentryDsn: process.env.SENTRY_DSN ?? '',
        sentryEnvironment: process.env.GETSOLAR_ENV ?? 'dev'
    })
);

function safeGetConfig(context: unknown): AppConfig {
    try {
        const ctx = context as Record<string, unknown> | undefined;
        if (ctx && typeof ctx.config === 'object' && ctx.config !== null) {
            return ctx.config as AppConfig;
        }
    } catch {
        // ignore
    }
    return {
        googleApiKey: '',
        googleTag: '',
        whatsappLink: '',
        posthogKey: '',
        posthogHost: '',
        sentryDsn: '',
        sentryEnvironment: 'dev'
    };
}

export const Route = createRootRoute({
    validateSearch: searchSchema,
    beforeLoad: async () => {
        const config = await getPublicConfig();
        // Init Sentry here (not in RootComponent's createEffect) so it's
        // ready before any component renders. If an error occurs during
        // initial load, TanStack Router renders errorComponent instead of
        // RootComponent — if we waited for createEffect, Sentry would
        // never init and the errorComponent's captureException would no-op.
        // The typeof-window guard inside initSentry makes this a no-op
        // during SSR.
        initSentry(config.sentryDsn, config.sentryEnvironment);
        return { config };
    },
    head: ({ context }) => {
        safeGetConfig(context); // validate context early

        return {
            meta: [
                { charset: 'utf-8' },
                {
                    name: 'viewport',
                    content: 'width=device-width, initial-scale=1'
                },
                { title: 'GetSolar: Solar Panel Calculator' },
                {
                    name: 'description',
                    content: 'Calculate your solar savings with GetSolar'
                }
            ],
            links: [
                { rel: 'stylesheet', href: appCss },
                { rel: 'icon', href: '/favicon.ico' },
                { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
                {
                    rel: 'preconnect',
                    href: 'https://fonts.gstatic.com',
                    crossOrigin: 'anonymous'
                },
                {
                    rel: 'stylesheet',
                    href: 'https://fonts.googleapis.com/css2?family=Figtree:wght@400;500;600;700&family=Open+Sans:wght@400;500;600;700&display=swap'
                }
            ],
            scripts: []
        };
    },
    errorComponent: (props) => {
        // Client-side capture on mount. Mirrors apps/web/app/global-error.tsx.
        onMount(() => captureException(props.error));
        return (
            <html lang={locale().localeString}>
                <head>
                    <HydrationScript />
                    <HeadContent />
                </head>
                <body class="font-sans text-brand-black bg-white antialiased">
                    <div class="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center">
                        <h1 class="text-xl font-semibold">
                            Something went wrong
                        </h1>
                        <p class="text-sm text-gray-600">
                            Please try again. If the problem persists, contact
                            support.
                        </p>
                    </div>
                    <Scripts />
                </body>
            </html>
        );
    },
    component: RootComponent
});

function SearchParamCapture() {
    const search = Route.useSearch();
    const [, actions] = useCalculator();

    createEffect(() => {
        const s = search();
        actions.setUtm({
            source: s.utm_source ?? null,
            medium: s.utm_medium ?? null,
            campaign: s.utm_campaign ?? null,
            term: s.utm_term ?? null,
            content: s.utm_content ?? null
        });
        actions.setReferralCode(s.referralCode ?? null);
    });

    return null;
}

function gtmSnippet(id: string): string {
    return (
        `(function(w,d,s,l,i){` +
        `w[l]=w[l]||[];` +
        `w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});` +
        `var f=d.getElementsByTagName(s)[0],` +
        `j=d.createElement(s),` +
        `dl=l!='dataLayer'?'&l='+l:'';` +
        `j.async=true;` +
        `j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;` +
        `f.parentNode.insertBefore(j,f);` +
        `})(window,document,'script','dataLayer',${JSON.stringify(id)});`
    );
}

function RootComponent() {
    const ctx = Route.useRouteContext() as unknown as () => {
        config: AppConfig;
    };
    const config = () => ctx().config;

    createEffect(() => {
        const { posthogKey, posthogHost } = config();
        if (posthogKey && posthogHost) {
            initPostHog(posthogKey, posthogHost);
        }
    });

    return (
        <html lang={locale().localeString}>
            <head>
                <HydrationScript />
                <HeadContent />
                <Show when={config().googleApiKey}>
                    <script
                        src={`https://maps.googleapis.com/maps/api/js?key=${config().googleApiKey}&libraries=places,drawing,geometry&v=weekly`}
                        async
                    />
                </Show>
                <Show when={config().googleTag}>
                    <script>{gtmSnippet(config().googleTag)}</script>
                </Show>
            </head>
            <body class="font-sans text-brand-black bg-white antialiased">
                <Show when={config().googleTag}>
                    <noscript>
                        <iframe
                            src={`https://www.googletagmanager.com/ns.html?id=${config().googleTag}`}
                            height="0"
                            width="0"
                            style={{ display: 'none', visibility: 'hidden' }}
                        />
                    </noscript>
                </Show>
                <CalculatorProvider>
                    <SearchParamCapture />
                    <Outlet />
                </CalculatorProvider>
                <Scripts />
            </body>
        </html>
    );
}
